import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Zap, Brain, Wind, Shield, TrendingDown, ListChecks, Target } from "lucide-react";
import { toast } from "sonner";
import { useNavigationHistory } from "@/hooks/useNavigationHistory";
import { PanicProtocol } from "./exercises/PanicProtocol";
import { ExposureHierarchy } from "./exercises/ExposureHierarchy";

interface AnxietyToolsProps {
  user: any;
}

export const AnxietyTools = ({ user }: AnxietyToolsProps) => {
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [worryTreeStep, setWorryTreeStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const { goBack, getBackButtonLabel } = useNavigationHistory();

  const tools = [
    {
      id: 'worry-tree',
      name: 'Interactive Worry Tree',
      icon: Brain,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Decision tree to sort worries into actionable vs non-actionable',
      timeframe: '5-10 min',
      category: 'Problem-Solving'
    },
    {
      id: 'panic-protocol',
      name: 'Panic Attack Protocol',
      icon: Zap,
      color: 'text-soft-accent',
      bgColor: 'bg-soft-accent/10',
      description: 'Step-by-step guide through panic attacks',
      timeframe: '5-15 min',
      category: 'Crisis Support'
    },
    {
      id: 'exposure-ladder',
      name: 'Exposure Hierarchy Builder',
      icon: TrendingDown,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      description: 'Gradually face fears with structured exposure',
      timeframe: '15-20 min',
      category: 'Long-term'
    },
    {
      id: 'worry-postpone',
      name: 'Worry Postponement',
      icon: ListChecks,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Schedule worry time instead of constant rumination',
      timeframe: '5 min setup',
      category: 'Daily Practice'
    },
    {
      id: 'safety-behaviours',
      name: 'Safety Behaviours Check',
      icon: Shield,
      color: 'text-soft-accent',
      bgColor: 'bg-soft-accent/10',
      description: 'Identify and gradually reduce safety behaviours',
      timeframe: '10-15 min',
      category: 'Awareness'
    },
    {
      id: 'anxiety-triggers',
      name: 'Trigger Tracker',
      icon: Target,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      description: 'Identify patterns and early warning signs',
      timeframe: '5-10 min',
      category: 'Tracking'
    }
  ];

  if (activeTool === 'panic-protocol') {
    return <PanicProtocol userId={user.id} onBack={() => setActiveTool(null)} />;
  }

  if (activeTool === 'exposure-ladder') {
    return <ExposureHierarchy userId={user.id} onBack={() => setActiveTool(null)} />;
  }

  if (activeTool === 'worry-tree') {
    const worryTreeSteps = [
      {
        question: 'What are you worried about?',
        placeholder: 'Describe your worry in detail...',
        guidance: 'Be specific. The more clearly you define the worry, the easier it is to work with.'
      },
      {
        question: 'Is there anything you can do about this right now?',
        type: 'choice',
        options: ['Yes, I can take action', 'No, it\'s out of my control'],
        guidance: 'Be honest. Many worries are about things we cannot directly control.'
      },
      {
        question: responses.step1 === 'Yes, I can take action' 
          ? 'What specific action can you take?' 
          : 'Can you let this worry go for now?',
        placeholder: responses.step1 === 'Yes, I can take action' 
          ? 'List concrete steps you can take...'
          : 'Practice saying: "This is not mine to control. I can let it go for now."',
        guidance: responses.step1 === 'Yes, I can take action'
          ? 'Break it down into the smallest possible first step.'
          : 'Letting go doesn\'t mean you don\'t care. It means you\'re choosing not to exhaust yourself with what you cannot change.'
      }
    ];

    const currentStep = worryTreeSteps[worryTreeStep];

    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={() => { setActiveTool(null); setWorryTreeStep(0); setResponses({}); }}>
          ← Back to Anxiety Tools
        </Button>

        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-primary/10">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-light">Worry Tree</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sort your worries into what you can and cannot control
                  </p>
                </div>
              </div>
              <Badge variant="secondary">Step {worryTreeStep + 1}/3</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Question */}
            <div className="bg-gradient-calm rounded-lg p-4 space-y-3">
              <p className="font-medium text-lg">{currentStep.question}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{currentStep.guidance}</p>
            </div>

            {/* Input Area */}
            {currentStep.type === 'choice' ? (
              <div className="grid gap-3">
                {currentStep.options?.map((option, idx) => (
                  <Button
                    key={idx}
                    variant={responses[`step${worryTreeStep}`] === option ? 'default' : 'outline'}
                    className="h-auto py-4 text-left justify-start"
                    onClick={() => {
                      setResponses({ ...responses, [`step${worryTreeStep}`]: option });
                      setTimeout(() => setWorryTreeStep(worryTreeStep + 1), 300);
                    }}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <Textarea
                  value={responses[`step${worryTreeStep}`] || ''}
                  onChange={(e) => setResponses({ ...responses, [`step${worryTreeStep}`]: e.target.value })}
                  placeholder={currentStep.placeholder}
                  className="min-h-[150px] bg-background/80"
                />
                {worryTreeStep < worryTreeSteps.length - 1 ? (
                  <Button 
                    onClick={() => setWorryTreeStep(worryTreeStep + 1)}
                    disabled={!responses[`step${worryTreeStep}`]?.trim()}
                    className="w-full"
                  >
                    Next Step
                  </Button>
                ) : (
                  <Button 
                    onClick={() => toast.success('Worry processing complete! Great work.')}
                    disabled={!responses[`step${worryTreeStep}`]?.trim()}
                    className="w-full"
                  >
                    Complete
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Science Behind Worry Tree */}
        <Card className="bg-gradient-calm border-border/50">
          <CardContent className="py-4">
            <div className="space-y-3">
              <p className="text-sm font-medium">The Science: Why the Worry Tree Works</p>
              <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
                <p>
                  <span className="font-medium text-foreground">Cognitive psychology research</span> shows that worry becomes problematic when it's 
                  focused on things outside your control. This creates a mental "spin cycle" that exhausts your brain's resources without solving anything. 
                  The Worry Tree interrupts this pattern by forcing a simple question: Can I act on this right now?
                </p>
                <p>
                  <span className="font-medium text-foreground">What happens in your brain:</span> Productive worry activates your prefrontal cortex 
                  (problem-solving center) and leads to action planning. Unproductive worry keeps your amygdala (fear center) activated without engaging 
                  solutions. By externalizing the worry onto paper and categorizing it, you shift from emotional to logical processing.
                </p>
                <p>
                  <span className="font-medium text-foreground">Benefits you may experience:</span> Reduced rumination, better sleep (worry interferes 
                  with sleep onset), improved focus on what you CAN control, and relief from the exhaustion of chronic worrying. Many people report 
                  feeling lighter after realising how many worries are actually outside their sphere of influence.
                </p>
                <p className="text-foreground">
                  This isn't about suppressing worry-it's about directing your mental energy where it can actually make a difference.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={goBack} size="lg" className="text-base mb-4">
        ← {getBackButtonLabel()}
      </Button>

      <div className="text-center mb-6">
        <h3 className="text-3xl font-light text-foreground mb-3">Anxiety & Panic Support</h3>
        <p className="text-base text-foreground/80 max-w-2xl mx-auto">
          Evidence-based tools to understand and manage anxiety, worry, and panic symptoms
        </p>
      </div>

      {/* Psycho-Education Card */}
      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardContent className="py-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Zap className="h-6 w-6 text-primary" />
              <p className="text-lg font-medium text-foreground">Understanding Anxiety: What's Actually Happening & Why These Tools Work</p>
            </div>
            
            <div className="space-y-3 text-base text-foreground leading-relaxed">
              <p>
                <span className="font-semibold">Anxiety isn't the enemy</span>-it's your nervous system's threat detection system working overtime. 
                Your amygdala (fear centre) is sounding alarms even when there's no real danger. This creates a cycle: anxious thoughts → body sensations 
                (racing heart, tight chest) → more anxious thoughts → more body sensations.
              </p>
              
              <p>
                <span className="font-semibold">The Neuroscience:</span> When anxiety strikes, your sympathetic nervous system activates 
                (fight/flight response). Blood rushes from your prefrontal cortex (thinking brain) to your muscles (preparing to run or fight). 
                This is why it's hard to think clearly when anxious-your brain is literally directing resources elsewhere.
              </p>
              
              <p>
                <span className="font-semibold">Why these tools work:</span> Grounding techniques activate your parasympathetic nervous system 
                (rest/digest), bringing your body back to baseline. Cognitive tools help your prefrontal cortex come back online so you can challenge 
                catastrophic thinking. Exposure gradually retrains your amygdala that feared situations are actually safe.
              </p>
              
              <p>
                <span className="font-semibold">The Worry Tree</span> works by externalising anxious thoughts, which interrupts rumination. 
                Research shows that asking "Can I do something about this right now?" prevents your brain from spinning in unproductive worry loops.
              </p>
              
              <p>
                <span className="font-semibold">Panic attacks</span> are your body's false alarm-they're terrifying but not dangerous. 
                They peak within 10 minutes because your body literally can't maintain that level of adrenaline longer. Learning this helps break 
                the fear-of-fear cycle that maintains panic disorder.
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-border/50">
              <Badge variant="secondary" className="text-xs">CBT-based</Badge>
              <Badge variant="secondary" className="text-xs">Exposure therapy</Badge>
              <Badge variant="secondary" className="text-xs">Nervous system regulation</Badge>
              <Badge variant="secondary" className="text-xs">Evidence-based</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tools Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <Card 
              key={tool.id}
              className="hover:shadow-therapy transition-all duration-300 cursor-pointer border-border/50"
              onClick={() => setActiveTool(tool.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-3 flex-1">
                     <div className={`p-3 rounded-lg ${tool.bgColor}`}>
                      <Icon className={`h-6 w-6 ${tool.color}`} />
                    </div>
                     <div>
                      <CardTitle className="text-xl mb-2 font-aileron text-foreground">{tool.name}</CardTitle>
                      <CardDescription className="text-base leading-relaxed text-foreground/80">{tool.description}</CardDescription>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs font-aileron">{tool.timeframe}</Badge>
                  <Badge variant="secondary" className="text-xs font-aileron">{tool.category}</Badge>
                </div>
              </CardHeader>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
